import { useEffect, useState } from 'react';
import { Search, Download, UserPlus, Users, ClipboardCheck, TriangleAlert, Clock, ChevronRight, Table2, LayoutGrid, Check, ArrowRight, Mail } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getInstitutionRequests } from '../../utils/remote';
import TopBarActions from '../../components/Header/TopBarActions';
import { useStudentsList, useBulkAssignSection } from '../../school_panel/api/hooks';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { SchoolApi } from '../../school_panel/api/client';
import EngagementDot from '../../school_panel/components/EngagementDot';
import WellbeingIndicator from '../../school_panel/components/WellbeingIndicator';
import { PanelCard, EmptyState, Spinner, ErrorPanel, PageHeader, Button, SortableTh, Segmented, PillTabs, Badge, PagedFooter, Checkbox, MetricCard, Track, Table, Th, Td, TextLinkButton } from '../../school_panel/components/ui';
import { FaUserGraduate } from '../../school_panel/components/ui/icons';
import { buildStudentsCsv } from './exportStudentsCsv';
import { downloadCsv } from '../../school_panel/pages/class_performance/export/csv';
import NoticeComposer from '../SchoolTasks/components/NoticeComposer';
import { useSortableTable, SortColumn } from '../../school_panel/lib/useSortableTable';
import { usePagination } from '../../school_panel/lib/usePagination';
import { relativeTime, ENGAGEMENT_LABEL, WELLBEING_LABEL, NO_CHECKINS_LABEL, fullNameOf, classLabel, Initials, RiskPill, AlertsChip } from '../../school_panel/components/studentRow';
import { TH_RULE } from '../../school_panel/lib/tableRules';
import TeachingTags from '../../school_panel/components/TeachingTags';
import { withTime } from '../../school_panel/lib/dates';

const STUDENT_COLUMNS: SortColumn<any>[] = [
  { key: 'name', accessor: (r) => `${r.first_name} ${r.last_name}`.trim() || r.username },
  // Class column: grade, then section; students without a section sort last.
  { key: 'grade', accessor: (r) => (r.section?.display ? `${r.grade_name ?? ''} | ${r.section.display}` : null) },
  { key: 'risk', accessor: (r) => r.risk_score, type: 'number' },
  { key: 'engagement', accessor: (r) => r.engagement_dot },
  { key: 'wellbeing', accessor: (r) => r.wellbeing_dot },
  { key: 'avg', accessor: (r) => r.avg_score, type: 'number' },
  { key: 'open_alerts', accessor: (r) => r.open_alerts_count, type: 'number' },
  { key: 'last_active', accessor: (r) => r.last_active_at, type: 'date' },
];

const RISK_BANDS = ['all', 'high', 'medium', 'low', 'insufficient_data'] as const;
type RiskBand = typeof RISK_BANDS[number];
const BAND_LABEL: Record<RiskBand, string> = { all: 'All', high: 'High', medium: 'Medium', low: 'Low', insufficient_data: 'No data' };
const BAND_DOT: Record<RiskBand, 'neutral' | 'danger' | 'warning' | 'success' | undefined> = {
  all: undefined, high: 'danger', medium: 'warning', low: 'success', insufficient_data: 'neutral',
};
const bandOf = (r: any): RiskBand => (r.risk_band === 'high' || r.risk_band === 'medium' || r.risk_band === 'low' ? r.risk_band : 'insufficient_data');
const SORT_NAME: Record<string, string> = {
  name: 'name', grade: 'class', risk: 'risk', engagement: 'engagement', wellbeing: 'wellbeing', avg: 'average score', open_alerts: 'open alerts', last_active: 'last active',
};
// The artboard pages the list 8 at a time (`PER = 8`).
const PAGE_SIZE = 8;

// SortableTh draws no rule of its own; the artboard header sits on a line-coloured rule.
const CARD_LABEL = 'font-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-muted';

const FIELD_WRAP = 'flex min-h-[44px] min-w-[150px] flex-1 items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent';
const FIELD_INPUT = 'min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-60';


type ViewMode = 'table' | 'cards';
const VIEW_OPTIONS = [
  { key: 'table' as ViewMode, label: 'Table', icon: <Table2 size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: 'cards' as ViewMode, label: 'Cards', icon: <LayoutGrid size={15} strokeWidth={1.8} aria-hidden="true" /> },
];


export default function Students() {
  const { role, token } = useAuth() as any;
  const teacherView = role === 'teacher';
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('table');
  // Deep link from Section detail ("All N students in 10B"): /students?grade=<id>&section=<id|none>.
  const [linkParams, setLinkParams] = useSearchParams();
  const [gradeId, setGradeId] = useState<number | 'all'>(() => {
    const raw = linkParams.get('grade');
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) ? n : 'all';
  });
  const [sectionVal, setSectionVal] = useState<number | 'all' | 'none'>(() => {
    const raw = linkParams.get('section');
    if (raw === 'none') return 'none';
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) ? n : 'all';
  });

  // Keep the URL in step with the filters (replace, not push) so a reload never restores a stale deep link.
  useEffect(() => {
    setLinkParams((prev) => {
      const next = new URLSearchParams(prev);
      if (gradeId === 'all') next.delete('grade'); else next.set('grade', String(gradeId));
      if (sectionVal === 'all') next.delete('section'); else next.set('section', String(sectionVal));
      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [gradeId, sectionVal, setLinkParams]);

  const isAdmin = role === 'school_admin' || role === 'school' || role === 'admin';
  const bulkMut = useBulkAssignSection();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [riskBandFilter, setRiskBandFilter] = useState<RiskBand>('all');
  const [messageOpen, setMessageOpen] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [pendingMove, setPendingMove] = useState<string | null>(null);
  const [moveResult, setMoveResult] = useState<{ moved: number; errors: string[]; failed: boolean } | null>(null);

  function toggleRow(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirmMove() {
    const value = pendingMove;
    setPendingMove(null);
    if (value === null || !selected.size) return;
    const section_id = value === 'none' ? null : Number(value);
    const ids = Array.from(selected);
    try {
      const res = await bulkMut.mutateAsync({ student_ids: ids, section_id });
      const errs = res.errors ?? [];
      const nameOf = (id: number) => {
        const r: any = allRows.find((x: any) => x.id === id);
        return r ? (`${r.first_name} ${r.last_name}`.trim() || r.username) : `student ${id}`;
      };
      setMoveResult({
        moved: res.moved ?? 0,
        errors: errs.map((e) => `Couldn't move ${nameOf(e.student_id)}: ${e.reason}`),
        failed: false,
      });
      // Keep only the students who could not be moved selected.
      setSelected(new Set(errs.map((e) => e.student_id)));
    } catch {
      setMoveResult({ moved: 0, errors: ['Move failed. Try again.'], failed: true });
    }
  }

  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });
  const grades = gradesQ.data?.grades ?? [];
  const sectionLabel = (g: any, s: any) => s.display || `${g.name} ${s.name}`;
  // Section filter: every section carries its grade in its label, so "All grades" is unambiguous.
  const sectionsForFilter = (gradeId === 'all' ? grades : grades.filter((g) => g.id === gradeId))
    .flatMap((g) => (g.sections ?? []).map((s) => ({ id: s.id, label: sectionLabel(g, s) })));

  const params: any = {};
  if (gradeId !== 'all') params.grade = gradeId;
  if (sectionVal === 'none') params.section = 'none';
  else if (sectionVal !== 'all') params.section = sectionVal;
  if (riskBandFilter !== 'all') { params.risk_band = riskBandFilter; }

  const q = useStudentsList(params);
  const pendingQ = useQuery({
    queryKey: ['institutionRequests'],
    queryFn: () => getInstitutionRequests(token),
    enabled: !!token && isAdmin,
  });
  const pendingCount = (pendingQ.data?.requests ?? []).filter((r: any) => r.status === 'pending').length;

  const allRows = q.data?.results ?? [];
  const filteredRows = allRows.filter((r: any) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    const name = `${r.first_name} ${r.last_name}`.toLowerCase();
    return name.includes(term) || r.username.toLowerCase().includes(term);
  });

  const { rows, sort, toggle } = useSortableTable(filteredRows, STUDENT_COLUMNS, { key: 'name', dir: 'asc' });
  const viewKey = `${search}|${gradeId}|${String(sectionVal)}|${riskBandFilter}|${sort?.key}${sort?.dir}`;
  const pg = usePagination(rows, PAGE_SIZE, viewKey);
  // Selection is per view (as on the admin Users page): changing page, filters
  // or sort clears it, so bulk actions never reach rows the user cannot see.
  useEffect(() => { setSelected(new Set()); }, [pg.page, viewKey]);
  // A half-made move choice never outlives the selection it was made for.
  useEffect(() => { setPendingMove(null); }, [selected]);

  const header = () => <PageHeader eyebrow="Overview" title="Students" subtitle="Browse students, check risk and engagement, and organise them into sections." />;
  if (q.isLoading) return <>{header()}<PanelCard><div className="flex justify-center py-8"><Spinner size="md" /></div></PanelCard></>;
  if (q.isError) return <>{header()}<PanelCard><ErrorPanel body="Could not load students. Try again." /></PanelCard></>;

  // Move targets: only sections of the grade the selected students are in.
  const selectedGradeIds = new Set(
    Array.from(selected)
      .map((id) => {
        const r: any = allRows.find((x: any) => x.id === id);
        if (!r) return null;
        if (r.grade_id != null) return r.grade_id;
        // Fall back to the grade that owns the student's current section.
        return grades.find((g) => (g.sections ?? []).some((sec) => sec.id === r.section?.id))?.id ?? null;
      })
      .filter((g) => g != null),
  );
  // Bulk notice: only preselect a grade when every selected student resolves to
  // it; a grade-less or unloaded student makes it behave like the mixed case.
  const allSelectedHaveGrade = Array.from(selected).every((id) => {
    const r: any = allRows.find((x: any) => x.id === id);
    return !!r && (r.grade_id != null || grades.some((g) => (g.sections ?? []).some((sec) => sec.id === r.section?.id)));
  });
  const noticeGradeId = selectedGradeIds.size === 1 && allSelectedHaveGrade ? (Array.from(selectedGradeIds)[0] as number) : null;
  const moveGrade = selectedGradeIds.size === 1 ? grades.find((g) => g.id === Array.from(selectedGradeIds)[0]) : undefined;
  const moveSections = (moveGrade?.sections ?? []).map((s) => ({ id: s.id, label: sectionLabel(moveGrade, s) }));
  const moveBlockedReason =
    selectedGradeIds.size > 1 ? 'The selected students are in different grades. Select one grade at a time to move them.'
    : selectedGradeIds.size === 0 ? 'The selected students have no grade, so they cannot be moved to a section.'
    : null;
  const pendingLabel = pendingMove === null ? '' : pendingMove === 'none' ? 'No section'
    : moveSections.find((s) => String(s.id) === pendingMove)?.label ?? 'this section';

  const allFilteredSelected = pg.pageItems.length > 0 && pg.pageItems.every((r: any) => selected.has(r.id));

  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        pg.pageItems.forEach((r: any) => next.delete(r.id));
      } else {
        pg.pageItems.forEach((r: any) => next.add(r.id));
      }
      return next;
    });
  }

  function exportCsv() {
    const toExport = selected.size > 0 ? rows.filter((r: any) => selected.has(r.id)) : rows;
    downloadCsv('students.csv', buildStudentsCsv(toExport));
  }

  const countLabel = rows.length === allRows.length
    ? `${allRows.length} student${allRows.length === 1 ? '' : 's'}`
    : `${rows.length} of ${allRows.length} students`;
  const highRiskCount = allRows.filter((r: any) => r.risk_band === 'high').length;
  const filtersActive = !!search || gradeId !== 'all' || sectionVal !== 'all' || riskBandFilter !== 'all';
  function clearFilters() {
    setSearch(''); setGradeId('all'); setSectionVal('all'); setRiskBandFilter('all');
  }

  // Band pill counts: with no band filter every band is countable from the loaded rows; while one is
  // active the list only holds that band, so only the active pill shows its count.
  const bandCount = (b: RiskBand): number | undefined => {
    if (riskBandFilter === 'all') return b === 'all' ? filteredRows.length : filteredRows.filter((r: any) => bandOf(r) === b).length;
    return b === riskBandFilter ? filteredRows.length : undefined;
  };
  const bandOptions = RISK_BANDS.map((b) => ({ key: b, label: BAND_LABEL[b], dot: BAND_DOT[b], count: bandCount(b) }));

  // KPI strip: figures come from the loaded list, so it is shown only while no band filter narrows it.
  const sectionIds = new Set(allRows.map((r: any) => r.section?.id).filter((x: any) => x != null));
  const withData = allRows.filter((r: any) => bandOf(r) !== 'insufficient_data').length;
  const mediumRiskCount = allRows.filter((r: any) => r.risk_band === 'medium').length;
  const inactiveCount = allRows.filter((r: any) => r.engagement_dot === 'silent').length;
  const withDataPct = allRows.length ? Math.round((withData / allRows.length) * 100) : 0;
  const showStrip = riskBandFilter === 'all' && allRows.length > 0;
  const atRiskCount = highRiskCount + mediumRiskCount;
  // Whole-school figures come from the grade list; once a grade or section narrows the rows, count what is loaded.
  const wholeSchool = gradeId === 'all' && sectionVal === 'all';
  const sectionTotal = grades.flatMap((g: any) => g.sections ?? []).length;
  const sectionCount = wholeSchool && sectionTotal > 0 ? sectionTotal : sectionIds.size;
  const gradeNums = grades.map((g: any) => Number(String(g.name).match(/\d+/)?.[0]));
  const gradeRange = wholeSchool && gradeNums.length > 0 && gradeNums.every(Number.isFinite)
    ? (Math.min(...gradeNums) === Math.max(...gradeNums) ? `grade ${gradeNums[0]}` : `grades ${Math.min(...gradeNums)}–${Math.max(...gradeNums)}`)
    : null;
  const sectionNoun = `${sectionCount} section${sectionCount === 1 ? '' : 's'}`;
  const subtitle = riskBandFilter === 'all' && wholeSchool && !search
    ? `${allRows.length} enrolled${sectionCount > 0 ? ` across ${sectionNoun}` : ''}. ${atRiskCount} ${atRiskCount === 1 ? 'is' : 'are'} at risk, ${highRiskCount} of them high.`
    : countLabel;
  // Average score comes from a newer backend field; without it the column and its sort stay out.
  const hasAvg = allRows.some((r: any) => r.avg_score !== undefined);

  // Design `.lbl`: dot + 500-weight label.
  const engagementCell = (r: any) => (
    <span className="inline-flex items-center gap-[7px] font-medium text-ink">
      <EngagementDot value={r.engagement_dot} />
      {ENGAGEMENT_LABEL[r.engagement_dot] ?? r.engagement_dot}
    </span>
  );
  const wellbeingCell = (r: any) => (
    <span className="inline-flex items-center gap-[7px] font-medium text-ink">
      <WellbeingIndicator value={r.wellbeing_dot} teacherView={teacherView} />
      {teacherView ? null : (r.wellbeing_dot ? (WELLBEING_LABEL[r.wellbeing_dot] ?? r.wellbeing_dot) : NO_CHECKINS_LABEL)}
    </span>
  );
  const avgCell = (r: any) => (
    <span className={`font-mono font-medium ${r.avg_score != null && r.avg_score < 50 ? 'text-danger' : ''}`}>
      {r.avg_score == null ? '—' : `${r.avg_score.toFixed(1)}%`}
    </span>
  );
  const rowCheckbox = (r: any, fullName: string) => (
    <Checkbox
      aria-label={`Select ${fullName}`}
      checked={selected.has(r.id)}
      onChange={() => toggleRow(r.id)}
    />
  );

  // Design `.banner` (success) inside a 22px wrapper; errors keep the same shape in danger colours.
  const banner = 'mx-[22px] mb-3.5 flex flex-wrap items-center gap-2.5 rounded-[14px] py-2.5 pl-3.5 pr-2.5 font-medium max-sm:mx-4';
  const okBanner = `${banner} bg-success-bg text-success`;
  const errBanner = `${banner} bg-danger-bg text-danger-ink`;
  const sortName = SORT_NAME[sort?.key ?? 'name'] ?? 'name';
  const sortSuffix = sort?.key === 'name' || sort?.key === 'grade'
    ? (sort.dir === 'asc' ? ' A→Z' : ' Z→A')
    : (sort?.dir === 'asc' ? ', low to high' : ', high to low');
  const fieldLabelCls = 'whitespace-nowrap text-[13px] text-muted';

  return (
    <>
      <PageHeader
        eyebrow="Students"
        title="Students"
        subtitle={subtitle}
        actions={(
          <>
            <Segmented label="View" options={VIEW_OPTIONS} value={view} onChange={setView} />
            {isAdmin && <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={exportCsv}>Export CSV</Button>}
          </>
        )}
      />
      {isAdmin && (
        <TopBarActions>
          <Link to="/students/requests"
            className="order-last inline-flex min-h-[44px] items-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy hover:brightness-105">
            <UserPlus size={18} strokeWidth={1.8} aria-hidden="true" />
            {pendingCount > 0 ? `Review ${pendingCount} join request${pendingCount === 1 ? '' : 's'}` : 'Join requests'}
          </Link>
        </TopBarActions>
      )}
      {showStrip && (
        <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-2 min-[1181px]:grid-cols-4">
          <MetricCard icon={<Users size={20} strokeWidth={1.8} />} label="Enrolled" value={allRows.length}
            sub={`${sectionNoun}${gradeRange ? ` · ${gradeRange}` : ''}`} />
          <MetricCard icon={<ClipboardCheck size={20} strokeWidth={1.8} />} label="With exam data"
            value={<>{withData} <small className="font-mono text-sm tracking-normal text-muted">{withDataPct}%</small></>}
            sub={<Track value={withDataPct} label="Students with exam data" className="!h-1.5" />} />
          <MetricCard tone="accent" icon={<TriangleAlert size={20} strokeWidth={1.8} />} label="At risk" value={highRiskCount + mediumRiskCount}
            sub={<span className="flex flex-wrap gap-1.5"><Badge tone="danger">{highRiskCount} high</Badge><Badge tone="warning">{mediumRiskCount} medium</Badge></span>} />
          <MetricCard icon={<Clock size={20} strokeWidth={1.8} />} label="Inactive 7+ days" value={inactiveCount} sub="silent for a week or more" />
        </div>
      )}
      <PanelCard className="!p-0">
        <div className="flex flex-wrap items-center justify-between gap-3.5 border-b border-hairline px-[22px] py-[18px] max-sm:px-4">
          <PillTabs
            badge
            label="Filter by band"
            value={riskBandFilter}
            onChange={setRiskBandFilter}
            options={bandOptions}
          />
          <div className="flex flex-[1_1_480px] flex-wrap justify-end gap-2.5">
            <div className={`${FIELD_WRAP} max-w-[320px] basis-[220px] max-sm:max-w-none`}>
              <Search size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
              <input id="students-search" type="search" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Name or username…" className={FIELD_INPUT} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-[26px] gap-y-3 border-b border-hairline px-[22px] py-3.5 max-sm:px-4">
          <div className="flex min-w-0 max-w-full items-center gap-2.5">
            <span className={fieldLabelCls}>Grade</span>
            <PillTabs scroll dense label="Grade" value={gradeId === 'all' ? 'all' : String(gradeId)}
              onChange={(k) => { setGradeId(k === 'all' ? 'all' : Number(k)); setSectionVal('all'); }}
              options={[{ key: 'all', label: 'All grades' }, ...grades.map((g: any) => ({ key: String(g.id), label: g.name }))]} />
          </div>
          {gradeId !== 'all' && (
            <div className="flex min-w-0 max-w-full items-center gap-2.5">
              <span className={fieldLabelCls}>Section</span>
              <PillTabs scroll dense label="Section" value={sectionVal === 'all' ? 'all' : String(sectionVal)}
                onChange={(k) => setSectionVal(k === 'all' || k === 'none' ? k : Number(k))}
                options={[
                  { key: 'all', label: 'All sections' },
                  { key: 'none', label: 'No section yet' },
                  ...sectionsForFilter.map((x) => ({ key: String(x.id), label: x.label })),
                ]} />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-[22px] pb-2 pt-3.5 text-[13px] text-muted max-sm:px-4">
          <span><b className="font-semibold text-ink">{countLabel}</b> · sorted by {sortName}{sortSuffix}</span>
          {isAdmin && <span>Tick students to move them to a section or message their families.</span>}
        </div>

        {msgSent && (
          <div role="status" className={okBanner}>
            <Check size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none" />
            <span>Notice sent</span>
            <TextLinkButton className="ml-auto" onClick={() => setMsgSent(false)}>Dismiss</TextLinkButton>
          </div>
        )}

        {moveResult && (
          <div role={moveResult.failed || moveResult.errors.length ? 'alert' : 'status'} className={moveResult.failed || moveResult.errors.length ? errBanner : okBanner}>
            {!(moveResult.failed || moveResult.errors.length) && <Check size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none" />}
            <div className="min-w-0 flex-1">
              {!moveResult.failed && <p>Moved {moveResult.moved} student{moveResult.moved === 1 ? '' : 's'}.</p>}
              {moveResult.errors.map((e, i) => <p key={i} className="break-words">{e}</p>)}
            </div>
            <TextLinkButton className="ml-auto" onClick={() => setMoveResult(null)}>Dismiss</TextLinkButton>
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState size="lg" icon={<FaUserGraduate />}
            title={filtersActive ? 'No students match' : 'No students'}
            body={!filtersActive ? 'No students are enrolled yet.' : search ? `Nothing matches \u201c${search}\u201d with these filters.` : 'No students match these filters. Try another band, grade or section.'}
            action={filtersActive ? <Button variant="ghost" onClick={clearFilters}>Clear filters</Button> : undefined} />
        ) : view === 'cards' ? (
          <>
          {isAdmin && (
            <label className="flex items-center gap-2 px-[22px] pt-1 text-xs font-semibold text-muted">
              <Checkbox aria-label="Select all on this page"
                     checked={allFilteredSelected} onChange={toggleSelectAll} />
              <span aria-hidden="true">Select all on this page</span>
            </label>
          )}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(236px,1fr))] gap-4 px-[22px] pb-1.5 pt-2 max-sm:px-4" role="list">
            {pg.pageItems.map((r: any) => {
              const fullName = fullNameOf(r);
              const on = selected.has(r.id);
              return (
                <article role="listitem" key={r.id} className={`flex flex-col gap-3 rounded-[18px] border bg-surface p-4 transition hover:-translate-y-0.5 hover:shadow-card-hover ${on ? 'border-navy shadow-[0_0_0_1px_rgb(var(--yd-ink))]' : 'border-line'}`}>
                  <div className="flex items-start justify-between gap-2.5">
                    <Initials name={fullName} large hi={r.risk_band === 'high'} />
                    {isAdmin && rowCheckbox(r, fullName)}
                  </div>
                  <div className="min-w-0">
                    <Link to={`/students/profile/${r.id}`} className="block break-words font-display text-base font-bold text-ink hover:underline hover:underline-offset-[3px]">{fullName}</Link>
                    <span className="text-xs text-muted">{classLabel(r.grade_name, r.section)} · @{r.username}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <TeachingTags teaching={r.teaching} />
                    <RiskPill r={r} />
                    <AlertsChip count={r.open_alerts_count} long />
                  </div>
                  <div className={`grid gap-2 border-t border-hairline pt-3 ${hasAvg ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {hasAvg && <div><span className={CARD_LABEL}>Avg</span><b className="mt-[5px] block text-sm">{avgCell(r)}</b></div>}
                    <div><span className={CARD_LABEL}>Mood</span><div className="mt-1.5 text-[13px]">{wellbeingCell(r)}</div></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted">
                    <span className="inline-flex items-center gap-[7px] font-normal">
                      <EngagementDot value={r.engagement_dot} />
                      {ENGAGEMENT_LABEL[r.engagement_dot] ?? r.engagement_dot}
                    </span>
                    <span>{relativeTime(r.last_active_at)}</span>
                  </div>
                </article>
              );
            })}
          </div>
          </>
        ) : (
          <Table minWidth={1000} wrapperClassName="px-3 pt-1">
            <thead>
              <tr>
                {isAdmin && (
                  <Th className="w-[34px]">
                    <Checkbox
                      aria-label="Select all on this page"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                    />
                  </Th>
                )}
                <SortableTh columnKey="name" sort={sort} onToggle={toggle} className={TH_RULE}>Student</SortableTh>
                <SortableTh columnKey="grade" sort={sort} onToggle={toggle} className={TH_RULE}>Class</SortableTh>
                <SortableTh columnKey="risk" sort={sort} onToggle={toggle} className={TH_RULE} help="risk_band">Risk</SortableTh>
                <SortableTh columnKey="engagement" sort={sort} onToggle={toggle} className={TH_RULE} help="engagement_dot">Engagement</SortableTh>
                <SortableTh columnKey="wellbeing" sort={sort} onToggle={toggle} className={TH_RULE} help="wellbeing_dot">Wellbeing</SortableTh>
                {hasAvg && <SortableTh columnKey="avg" sort={sort} onToggle={toggle} align="right" className={TH_RULE}>Avg score</SortableTh>}
                <SortableTh columnKey="open_alerts" sort={sort} onToggle={toggle} align="right" className={TH_RULE} help="open_alerts">Alerts</SortableTh>
                <SortableTh columnKey="last_active" sort={sort} onToggle={toggle} className={TH_RULE} help="last_active">Last active</SortableTh>
                <Th><span className="sr-only">Open profile</span></Th>
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((r: any) => {
                const fullName = fullNameOf(r);
                const on = selected.has(r.id);
                return (
                  <tr key={r.id} className={on ? '[&>td]:bg-row-sel' : ''}>
                    {isAdmin && <Td>{rowCheckbox(r, fullName)}</Td>}
                    <Td>
                      <div className="flex items-center gap-3">
                        <Initials name={fullName} hi={r.risk_band === 'high'} />
                        <div className="min-w-0">
                          <Link to={`/students/profile/${r.id}`} className="block font-semibold text-ink hover:underline hover:underline-offset-[3px]">{fullName}</Link>
                          <span className="block text-xs text-muted">@{r.username}</span>
                        </div>
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap font-mono font-medium">
                      {classLabel(r.grade_name, r.section)}
                      <span className="mt-1 block font-sans"><TeachingTags teaching={r.teaching} /></span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-2.5">
                        <RiskPill r={r} />
                        {r.risk_band && r.risk_band !== 'insufficient_data' && r.risk_score != null && (
                          <span aria-hidden="true" className="block h-1.5 w-[54px] flex-none overflow-hidden rounded-[3px] bg-track">
                            <span className={`block h-full rounded-[3px] ${r.risk_band === 'high' ? 'bg-danger' : r.risk_band === 'medium' ? 'bg-idle' : 'bg-slate'}`}
                                  style={{ width: `${Math.max(0, Math.min(100, r.risk_score))}%` }} />
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td>{engagementCell(r)}</Td>
                    <Td>{wellbeingCell(r)}</Td>
                    {hasAvg && <Td align="right">{avgCell(r)}</Td>}
                    <Td align="right"><AlertsChip count={r.open_alerts_count} /></Td>
                    <Td className="whitespace-nowrap text-[13px] text-muted"
                        title={r.last_active_at ? withTime(r.last_active_at) : undefined}>
                      {relativeTime(r.last_active_at)}
                    </Td>
                    <Td align="right">
                      {/* Decorative: the name link is the keyboard and screen-reader route. */}
                      <Link to={`/students/profile/${r.id}`} aria-hidden="true" tabIndex={-1}
                        className="ml-auto grid h-9 w-9 place-items-center rounded-[10px] text-muted hover:bg-mist hover:text-ink">
                        <ChevronRight size={18} strokeWidth={1.8} />
                      </Link>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {rows.length > 0 && (
          <PagedFooter divider="card" className="max-sm:!px-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Students pagination" />
        )}
      </PanelCard>
      {isAdmin && selected.size > 0 && (
        <div role="region" aria-label="Bulk actions"
             className="sticky bottom-4 z-[5] mt-4 flex flex-wrap items-center gap-2.5 rounded-[18px] bg-navy py-2.5 pl-[18px] pr-3 text-white shadow-actbar">
          {pendingMove !== null && !moveBlockedReason && (
            <div role="alertdialog" aria-label="Confirm move" className="flex basis-full flex-wrap items-center gap-2.5 rounded-[12px] bg-navy-2 px-3 py-2.5 text-mist-fixed">
              <span className="flex-[1_1_240px]">
                <b>Move {selected.size} student{selected.size === 1 ? '' : 's'} to {pendingLabel}?</b>{' '}
                <span className="text-[13px] text-on-navy">Roll numbers stay the same. Families are not notified.</span>
              </span>
              <Button variant="ghost-on-navy" size="sm" onClick={() => setPendingMove(null)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={confirmMove}>Move</Button>
            </div>
          )}
          <span className="font-semibold">{selected.size} selected</span>
          {!moveBlockedReason && (
            <label className="flex min-h-[40px] items-center gap-2 rounded-[12px] border border-navy-3 bg-navy-2 px-3 text-on-navy focus-within:ring-2 focus-within:ring-accent">
              <ArrowRight size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none" />
              <select
                aria-label="Move to section"
                value=""
                onChange={(e) => setPendingMove(e.target.value)}
                className="border-0 bg-transparent text-sm font-medium text-white outline-none [&_option]:text-ink">
                <option value="" disabled>Move to section…</option>
                <option value="none">No section</option>
                {moveSections.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>
          )}
          {moveBlockedReason && (
            <span className="text-[13px] text-on-navy">
              {selectedGradeIds.size > 1 ? 'Mixed grades selected. Pick one grade to move students.' : moveBlockedReason}
            </span>
          )}
          <Button variant="primary" icon={<Mail size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={() => setMessageOpen(true)}>Message…</Button>
          <Button variant="on-navy" icon={<Download size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={exportCsv} aria-label="Export selected">Export</Button>
          <span className="flex-1" />
          <Button variant="ghost-on-navy" onClick={() => setSelected(new Set())}>Clear selection</Button>
        </div>
      )}
      <NoticeComposer
        isOpen={messageOpen}
        onClose={() => setMessageOpen(false)}
        isAdmin={isAdmin}
        initial={{
          mode: 'students',
          gradeId: noticeGradeId,
          studentIds: noticeGradeId != null ? Array.from(selected) : [],
        }}
        onSent={() => { setMsgSent(true); setSelected(new Set()); }}
      />
    </>
  );
}
